import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import dayjs from "dayjs-local";
import firebase from "firebaseFactory";
import {
  AthenaResults,
  AthenaResultsSource,
  fetchAthenaResults,
  MaximumDownloadSizeExceededError,
} from "components/athena/FetchAthenaResults";
import { DateTimePicker } from "components/DateTimePicker";
import { HighchartsGrid } from "components/highcharts/HighchartsGrid";
import { getHighchartOptionsFromAthena } from "components/highcharts/HighchartOptionsFromAthena";
import {
  extractEventDataFromAthenaResults,
  extractPlotlinesFromEventData,
} from "modules/events";
import { useAuthDataContext, Permissions } from "modules/auth";
import { GroupedToggleList } from "components/GroupedToggleList";
import { Button } from "components/Button";
import { ExternalLinkButton } from "components/ExternalLinkButton";
import { RadioButton } from "components/RadioButtons";
import { Toggle } from "components/Toggle";
import { getHighchartOptionsForCsv } from "components/highcharts/HighchartOptionsFromCsv";

import { Page } from "../layout/Page";

import { GridListToggle } from "./components/GridListToggle";
import {
  cellDataFields,
  packDataFields,
  unitDataFields,
  unitStateFields,
} from "./components/AvailableDataFields";

const recommendedMaximumDownloadSizeInKB = 1024 * 4;

interface SelectedDataFields {
  dataFields: string[];
  dataSource: string;
  cellIDs: string;
  includeEventData: boolean;
}
const SelectDataFields = ({
  onChangeDataFields,
}: {
  onChangeDataFields: (data: SelectedDataFields) => void;
}) => {
  const { enforcer } = useAuthDataContext();
  const permittedDataSources = enforcer
    .getDataFiltersFor(Permissions.Devices_ViewReports)
    .getForField("dataSource");
  const dataSourcesWithoutPermissionCheck = [
    {
      key: "pack_data",
      name: "Pack data",
      items: packDataFields,
      defaults: ["grid_current_a"],
    },
    {
      key: "cell_data",
      name: "Cell data",
      items: cellDataFields,
      defaults: ["cell_temperature_c"],
    },
    {
      key: "unit_power_data",
      name: "Unit data",
      items: unitDataFields,
      defaults: ["grid_voltage_v"],
    },
    {
      key: "unit_state_data",
      name: "Unit state data",
      items: unitStateFields,
      defaults: [],
    },
  ];

  const [includeEventData, setIncludeEventData] = useState(false);

  const dataSources = dataSourcesWithoutPermissionCheck.filter(
    ({ key }) =>
      permittedDataSources.$any || permittedDataSources.$in.includes(key)
  );

  const defaultDataSourceFields = Object.assign(
    {},
    ...dataSources.map(({ key, defaults }) => ({ [key]: defaults }))
  );

  const [selectedDataSource, setSelectedDataSource] = useState(
    dataSources[0].key
  );
  const [selectedFields, setSelectedFields] = useState<{
    [dataSource: string]: string[];
  }>(defaultDataSourceFields);

  const [selectedCellIDs, setSelectedCellIDs] = useState("1-144");

  useEffect(() => {
    onChangeDataFields({
      dataFields: selectedFields[selectedDataSource],
      dataSource: selectedDataSource,
      cellIDs: selectedCellIDs,
      includeEventData,
    });
  }, [
    selectedDataSource,
    selectedFields,
    includeEventData,
    onChangeDataFields,
    selectedCellIDs,
  ]);
  return (
    <>
      <div className="flex w-full">
        {dataSources.length > 1 ? (
          <div className="mr-6">
            <span className="block text-sm font-bold mb-2">Data source</span>
            {dataSources.map((dataSource) => (
              <button
                key={dataSource.key}
                onClick={() => setSelectedDataSource(dataSource.key)}
                className={`${
                  selectedDataSource === dataSource.key
                    ? "bg-gray-400"
                    : "hover:bg-gray-400"
                } text-sm focus:outline-none focus:shadow-outline text-gray-800 font-semibold py-2 mr-2 px-4 rounded`}
              >
                {dataSource.name}
              </button>
            ))}
          </div>
        ) : null}
        <div className="mr-2">
          <span className="block text-sm font-bold mb-2">Events</span>
          <Toggle
            name="includeEventData"
            text="Include event data"
            isSelected={includeEventData}
            onChange={(e) => setIncludeEventData(e.target.checked)}
          />
        </div>

        {selectedDataSource === "cell_data" ? (
          <div className="mr-6">
            <span className="block text-sm font-bold mb-2">Cell Selection</span>
            <input
              type="text"
              value={selectedCellIDs}
              onChange={(event) => setSelectedCellIDs(event.target.value)}
            />
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap w-full mb-6">
        <GroupedToggleList
          toggleGroups={
            dataSources.find((x) => x.key === selectedDataSource)?.items || {}
          }
          selectedToggles={selectedFields[selectedDataSource]}
          onChange={(selectedFields) =>
            setSelectedFields((state) => ({
              ...state,
              [selectedDataSource]: selectedFields,
            }))
          }
        />
      </div>
    </>
  );
};
interface ReportsPageQueryCriteria {
  fromDate: number;
  toDate: number;
  averagingWindow: number;
  selectedDataFields: SelectedDataFields;
  selectedCellIDs: string;
}

const ReportsPageQuery = ({
  onClickSubmit,
  isLoading,
  getDate = () => Date.now(),
}: {
  isLoading: boolean;
  getDate: () => number;
  onClickSubmit: (queryCriteria: ReportsPageQueryCriteria) => void;
}) => {
  const [selectedDataFields, setSelectedDataFields] = useState<
    SelectedDataFields
  >({
    dataFields: [],
    dataSource: "pack_data",
    includeEventData: true,
    cellIDs: "1",
  });

  const defaultToDate = useRef(getDate()).current;
  const getOffsetFromDefaultToDate = (offset: number, unit: dayjs.OpUnitType) =>
    dayjs(defaultToDate).add(-offset, unit).valueOf();

  const [fromDate, setFromDate] = useState(
    getOffsetFromDefaultToDate(1, "day")
  );
  const [toDate, setToDate] = useState(defaultToDate);
  const [definedAveragingWindow, setDefinedAveragingWindow] = useState<
    number | undefined
  >();

  const timeframes = [
    { title: "1H", unit: "hours", number: 1 },
    { title: "6H", unit: "hours", number: 6 },
    { title: "1D", unit: "day", number: 1 },
    { title: "1W", unit: "week", number: 1 },
    { title: "1M", unit: "month", number: 1 },
  ] as { title: string; unit: dayjs.OpUnitType; number: number }[];
  const mappedTimeframes = timeframes.map(({ title, unit, number }, index) => {
    const offset = getOffsetFromDefaultToDate(number, unit);
    const selected = toDate === defaultToDate && fromDate === offset;
    return { offset, selected, title };
  });

  const calculateAveragingWindow = (fromDate: number, toDate: number) => {
    const targetDataPoints = 3000;
    const totalSecondsToPlot = (toDate - fromDate) / 1000;
    // limit to nearest second, as we've seen weird athena ordering
    // issues when sending fractional seconds
    return Math.round(totalSecondsToPlot / targetDataPoints);
  };
  const calculatedAveragingWindow = calculateAveragingWindow(fromDate, toDate);

  return (
    <div className="bg-white p-4 rounded">
      <SelectDataFields onChangeDataFields={setSelectedDataFields} />
      <div className="mb-6 flex flex-wrap w-full items-end">
        <div className="w-48 mr-2">
          <label className="block text-sm font-bold mb-2" htmlFor="fromDate">
            From date
          </label>
          <DateTimePicker
            name="fromDate"
            currentDate={fromDate}
            maxDate={defaultToDate}
            onChange={setFromDate}
          />
        </div>
        <div className="w-48 mr-2">
          <label className="block text-sm font-bold mb-2" htmlFor="toDate">
            To date
          </label>
          <DateTimePicker
            name="toDate"
            currentDate={toDate}
            maxDate={defaultToDate}
            onChange={setToDate}
          />
        </div>
        <div className="mr-2">
          <span className="block text-sm font-bold mb-2">Timeframe</span>
          {mappedTimeframes.map(({ title, offset, selected }, index) => (
            <RadioButton
              key={index}
              onClick={() => {
                setToDate(defaultToDate);
                setFromDate(offset);
              }}
              isSelected={selected}
            >
              {title}
            </RadioButton>
          ))}
        </div>
        <div className="mr-2">
          <span className="block text-sm font-bold mb-2">Averaging</span>
          <RadioButton
            isSelected={!definedAveragingWindow}
            onClick={() => {
              setDefinedAveragingWindow(undefined);
            }}
          >
            Auto
          </RadioButton>
          <RadioButton
            isSelected={definedAveragingWindow !== undefined}
            onClick={() => {
              setDefinedAveragingWindow(calculatedAveragingWindow);
            }}
          >
            Custom
          </RadioButton>
          <div className="ml-2 inline-block">
            {!definedAveragingWindow ? (
              <span className="text-gray-600 inline-block text-xs italic">
                {calculatedAveragingWindow} second averaging.
              </span>
            ) : (
              <>
                <input
                  type="number"
                  className="w-20 border-gray-200 focus:border-gray-500 appearance-none bg-gray-200 border rounded py-1 px-2 leading-tight focus:outline-none focus:bg-white"
                  onChange={(e) =>
                    setDefinedAveragingWindow(e.target.valueAsNumber)
                  }
                  value={definedAveragingWindow}
                />
                <span className="text-gray-600 ml-2 inline-block text-xs italic">
                  second averaging.
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex w-full">
        <input
          type="submit"
          disabled={isLoading}
          onClick={() =>
            onClickSubmit({
              fromDate,
              toDate,
              averagingWindow:
                definedAveragingWindow || calculatedAveragingWindow,
              selectedDataFields,
              selectedCellIDs: "1",
            })
          }
          value="Run query"
          className={`${
            isLoading
              ? "cursor-not-allowed bg-blue-200"
              : "cursor-pointer bg-blue-500 hover:bg-blue-700"
          } translate-y-full text-white font-bold py-2 px-4 rounded sel focus:outline-none focus:shadow-outline`}
        />
      </div>
    </div>
  );
};

export const ReportsPage = ({
  getHistoricalData = firebase.function("getHistoricalData"),
  getDate = () => Date.now(),
}) => {
  const { siteId, unitId, deviceId } = useParams();
  const [viewAsGrid, setViewAsGrid] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>();

  const [athenaResultsSource, setAthenaResultsSource] = useState<
    AthenaResultsSource | undefined
  >();
  const [athenaResults, setAthenaResults] = useState<
    AthenaResults | undefined
  >();
  const [queryCriteria, setQueryCriteria] = useState<
    ReportsPageQueryCriteria | undefined
  >();

  const loadAthenaResultsFromSource = async (
    athenaResultsSource: AthenaResultsSource | undefined,
    ignoreDownloadLimit: boolean
  ) => {
    if (!athenaResultsSource) return;
    setLoading(true);
    const results = await fetchAthenaResults(athenaResultsSource, {
      onStatusUpdate: setStatus,
      maximumDownloadSizeInKB: ignoreDownloadLimit
        ? undefined
        : recommendedMaximumDownloadSizeInKB,
    });
    setLoading(false);
    setStatus("Done");
    return results;
  };
  const renderAthenaResultsIgnoringDownloadLimit = async () => {
    const athenaResults = await loadAthenaResultsFromSource(
      athenaResultsSource,
      true
    );
    setAthenaResults(athenaResults);
  };

  const hasExceededDownloadSize =
    athenaResults &&
    athenaResults.error &&
    athenaResults.error instanceof MaximumDownloadSizeExceededError;

  const eventData = athenaResults
    ? extractEventDataFromAthenaResults(athenaResults)
    : [];
  const plotLines = extractPlotlinesFromEventData(eventData);

  const highchartOptions =
    athenaResults &&
    queryCriteria &&
    queryCriteria.selectedDataFields.dataFields.length > 0
      ? getHighchartOptionsFromAthena(
          queryCriteria.selectedDataFields.dataFields,
          athenaResults,
          plotLines
        )
      : [
          getHighchartOptionsForCsv({
            parsedRows: [],
            columns: [],
            plotLines,
          }),
        ];

  const onClickSubmit = async (queryCriteria: ReportsPageQueryCriteria) => {
    setLoading(true);
    setQueryCriteria(queryCriteria);
    setStatus("Waiting for query...");

    const generateArray = (min: number, max: number) =>
      Array.from({ length: max - min + 1 }, (_, i) => min + i);

    const logicalOperatorsToArray = (operations: string) => {
      const arrayString = operations.split(",");
      const arrayOfArrays = arrayString.map((operation) => {
        const range = operation.split("-");
        const start = range[0];
        const end = range[1];
        const array =
          end === undefined
            ? [Number(start)]
            : generateArray(Number(start), Number(end));
        return array;
      });

      const result = arrayOfArrays
        .reduce((accumulator, value) => accumulator.concat(value), [])
        .filter(function (elem, index, self) {
          return index === self.indexOf(elem);
        })
        .map(String);
      return result;
    };

    const cellIDs = logicalOperatorsToArray(
      queryCriteria.selectedDataFields.cellIDs
    );

    const result = await getHistoricalData.action({
      fromDate: queryCriteria.fromDate,
      toDate: queryCriteria.toDate,
      dataSource: queryCriteria.selectedDataFields.dataSource,
      dataFields: queryCriteria.selectedDataFields.dataFields,
      includeEventData: queryCriteria.selectedDataFields.includeEventData,
      averagingWindow: queryCriteria.averagingWindow,
      siteId: siteId,
      unitId: unitId,
      deviceId: deviceId,
      cellIDs: cellIDs,
    });
    if (result.data.error) {
      setStatus(result.data.error);
      setLoading(false);
      return;
    }
    const athenaResultsSource = result.data as AthenaResultsSource;
    const athenaResults = await loadAthenaResultsFromSource(
      athenaResultsSource,
      false
    );
    setAthenaResultsSource(athenaResultsSource);
    setAthenaResults(athenaResults);
  };

  return (
    <Page status={status}>
      <ReportsPageQuery
        getDate={getDate}
        onClickSubmit={onClickSubmit}
        isLoading={isLoading}
      />
      {athenaResultsSource && (
        <>
          <div className="py-6 inline-flex justify-end w-full">
            <div className="mr-4">
              <ExternalLinkButton href={athenaResultsSource.url} inverted>
                Download CSV
              </ExternalLinkButton>
            </div>
            <GridListToggle viewAsGrid={viewAsGrid} onChange={setViewAsGrid} />
          </div>
          {hasExceededDownloadSize ? (
            <div className="text-s mb-4 p-4 bg-gray-200 flex">
              <div>
                <p className="block mb-4">
                  {
                    (athenaResults?.error as MaximumDownloadSizeExceededError)
                      ?.message
                  }{" "}
                  You may experience slow performance rendering this volume of
                  data in the browser.
                </p>
                <ExternalLinkButton
                  className="mr-2"
                  href={athenaResultsSource.url}
                >
                  Download CSV
                </ExternalLinkButton>{" "}
                /* ... table of events, link to data */
                <Button onClick={renderAthenaResultsIgnoringDownloadLimit}>
                  Render graphs anyway
                </Button>
              </div>
            </div>
          ) : (
            <HighchartsGrid
              options={highchartOptions}
              viewAsGrid={viewAsGrid}
            />
          )}
        </>
      )}
    </Page>
  );
};
